import React, { useState, useEffect } from 'react';
import { Outlet, Navigate, useLocation } from 'react-router-dom';
import Header from './Header';
import Sidebar from './Sidebar';
import { useAuth } from '../../context/AuthContext';
import { STRIPE_PRODUCTS } from '../../stripe-config';
import { createCheckoutSession } from '../../lib/stripe';
import Button from '../../components/UI/Button';
import { ArrowRight } from 'lucide-react';

const Layout: React.FC = () => {
  const { isAuthenticated, isLoading, hasActiveSubscription, user } = useAuth();
  const location = useLocation();
  const [isAnnual, setIsAnnual] = useState(true);
  
  // If still loading or subscription status is unknown, show loading state
  if (isLoading || hasActiveSubscription === null) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
          <p className="text-sm text-gray-600 dark:text-gray-400">Chargement...</p>
        </div>
      </div>
    );
  }
  
  // If not authenticated, redirect to login
  if (!isAuthenticated || !user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // If subscription tier is null or hasActiveSubscription is false, show subscription page
  if (!hasActiveSubscription || user.subscription_tier === null) {
    const handleSubscribe = async (plan: 'ESSENTIAL' | 'PREMIUM') => {
      try {
        const productKey = `${plan}${isAnnual ? '_ANNUAL' : ''}` as keyof typeof STRIPE_PRODUCTS;
        const url = await createCheckoutSession(
          plan,
          STRIPE_PRODUCTS[productKey].priceId,
          `${window.location.origin}/app/account`,
          `${window.location.origin}/`
        );
        
        if (!url) {
          throw new Error('Failed to create checkout session');
        }
        
        window.location.href = url;
      } catch (error: any) {
        console.error('Error creating checkout session:', error);
        if (error.message === 'No active session found') {
          window.location.href = '/login';
        }
      }
    };

    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="text-center max-w-lg px-4">
          <h2 className="text-2xl font-bold mb-4">Choisissez votre abonnement</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-8">
            Pour accéder à toutes les fonctionnalités de Invoice AI, veuillez sélectionner un abonnement.
          </p>

          <div className="flex items-center justify-center gap-4 mb-12">
            <span className={`text-sm ${!isAnnual ? 'text-gray-900 dark:text-white font-semibold' : 'text-gray-500 dark:text-gray-400'}`}>
              Mensuel
            </span>
            <button
              onClick={() => setIsAnnual(!isAnnual)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                isAnnual ? 'bg-primary-500' : 'bg-gray-200 dark:bg-gray-700'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  isAnnual ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
            <span className={`text-sm ${isAnnual ? 'text-gray-900 dark:text-white font-semibold' : 'text-gray-500 dark:text-gray-400'}`}>
              Annuel <span className="text-primary-500">(2 mois offerts)</span>
            </span>
          </div>

          <div className="space-y-4">
            <div className="p-6 bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700">
              <h3 className="text-xl font-semibold mb-2">Abonnement Essentiel</h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Parfait pour démarrer avec Invoice AI
              </p>
              <div className="text-3xl font-bold mb-4">
                {isAnnual ? '31' : '37'}€ <span className="text-lg text-gray-500">/mois</span>
              </div>
              {isAnnual && (
                <p className="text-sm text-primary-600 dark:text-primary-400 mb-4">
                  Économisez 74€ par an
                </p>
              )}
              <Button
                variant="primary"
                fullWidth
                onClick={() => handleSubscribe('ESSENTIAL')}
                rightIcon={<ArrowRight className="h-4 w-4" />}
              >
                Commencer maintenant
              </Button>
            </div>
            
            <div className="p-6 bg-primary-50 dark:bg-primary-900/20 rounded-lg shadow-md border-2 border-primary-500">
              <div className="absolute top-0 right-0 bg-primary-500 text-white px-3 py-1 rounded-bl-lg rounded-tr-lg text-sm font-medium">
                Recommandé
              </div>
              <h3 className="text-xl font-semibold mb-2">Abonnement Premium</h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Toutes les fonctionnalités avancées
              </p>
              <div className="text-3xl font-bold mb-4">
                {isAnnual ? '39' : '47'}€ <span className="text-lg text-gray-500">/mois</span>
              </div>
              {isAnnual && (
                <p className="text-sm text-primary-600 dark:text-primary-400 mb-4">
                  Économisez 96€ par an
                </p>
              )}
              <Button
                variant="primary"
                fullWidth
                onClick={() => handleSubscribe('PREMIUM')}
                rightIcon={<ArrowRight className="h-4 w-4" />}
              >
                Choisir Premium
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Check if required fields are missing
  const requiredFieldsMissing = !user.company || !user.address || !user.postal_code || !user.city || !user.siret;
  
  // Redirect to account settings if required fields are missing
  if (hasActiveSubscription && requiredFieldsMissing && location.pathname !== '/app/account') {
    return <Navigate to="/app/account" replace />;
  }
  
  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-900">
      <Sidebar />
      <div className="flex flex-col flex-1 overflow-hidden">
        <Header />
        <main className="flex-1 overflow-y-auto p-6">
          <div className="container mx-auto">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;